/**
 * Multi-chain token configuration for supported stablecoins and tokens
 * Contains contract addresses for different networks
 */

export interface TokenConfig {
  symbol: string;
  name: string;
  decimals: number;
  addresses: {
    [chainId: number]: string | null; // null indicates native token
  };
  logoUrl?: string;
  isStablecoin: boolean;
  coingeckoId?: string;
}

export interface TokenAddresses {
  [tokenSymbol: string]: TokenConfig;
}

/**
 * Multi-chain token configurations
 */
// Placeholder token addresses that need real deployments
export const PLACEHOLDER_ADDRESSES = {
  USDP_BSC_PLACEHOLDER: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', // BUSD used as placeholder
  ZERO_ADDRESS: '0x0000000000000000000000000000000000000000'
};

const svgFiles = [
  // Original tokens from task - using only files that actually exist
  'alice.svg',
  'astr.svg',
  'bchsv.svg',
  'dodo.svg',
  'eos.svg',
  'eosdown.svg',
  'ETH.svg', // Use uppercase version that exists
  'ethbull.svg',
  'fil.svg',
  'USDC.svg',
  'USDT.svg',
  'USDP.png',
  'USDP.svg',
  'fxs.svg',
  'ilv.svg',
  'ksm.svg',
  'mana.svg',
  'mc.svg',
  'mkr.svg',
  'nas.svg',
  'near.svg',
  'nft.svg',
  'nu.svg',
  'onx.svg',
  'oxt.svg',
  'sbtc.svg',
  'sushidown.svg',
  'uniup.svg',
  'USD.svg', // Use uppercase version that exists
  'wings.svg',
  'xpr.svg',
  'yfii.svg',
  'yoyo.svg',
  'zrx.svg',
  // Popular main tokens - using only files that actually exist
  'aave.svg',
  'avax.svg',
  'band.svg',
  'BCH.svg', // Use uppercase version that exists
  'BNB.svg', // Use uppercase version that exists
  'BTC.svg', // Use uppercase version that exists
  'btg.svg',
  'chz.svg',
  'dai.svg',
  'ftm.svg',
  'qtum.svg',
  'sand.svg',
  'stx.svg',
  'uni.svg',
  'waves.svg',
  'wbtc.svg',
  'weth.svg',
  'wsteth.svg',
  'tusd.svg',
  'usdp.svg',
  'LINK.svg' 
];

const existingTokens: TokenAddresses = {
  USDP: {
    symbol: 'USDP',
    name: 'USD.P E-Pay Dollar',
    decimals: 18,
    addresses: {
      1: '0x8E870D67F660D95d5be530380D0eC0bd388289E1',
      56: '0x5bC001A73Db235D9e02606B7C53a1490f864d0B8', // USD.P on BSC
      11155111: '0x8E870D67F660D95d5be530380D0eC0bd388289E1',
      97: PLACEHOLDER_ADDRESSES.USDP_BSC_PLACEHOLDER,
      9999: 'USDPSaqUFkY2BNXHenNborN9HGUi5H4XuyTr4PSftq1' // USD.P on Solana
    },
    logoUrl: '/USDPlogo.png',
    isStablecoin: true,
    coingeckoId: 'paxos-standard'
  },
  USDT: {
    symbol: 'USDT',
    name: 'Tether USD',
    decimals: 6,
    addresses: {
      1: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      56: '0x55d398326f99059fF775485246999027B3197955',
      11155111: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS,
      97: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS
    },
    logoUrl: '/icons/tusd.svg',
    isStablecoin: true,
    coingeckoId: 'tether'
  },
  USDC: {
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    addresses: {
      1: '0xA0b86a33E564e47093307C26F597B33Fa5E1Cd7E',
      56: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      11155111: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS,
      97: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS
    },
    logoUrl: '/icons/USDC.svg',
    isStablecoin: true,
    coingeckoId: 'usd-coin'
  },
  ETH: {
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    addresses: {
      1: null,
      56: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
      11155111: null,
      97: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS
    },
    logoUrl: '/icons/ETH.svg',
    isStablecoin: false,
    coingeckoId: 'ethereum'
  },
  BNB: {
    symbol: 'BNB',
    name: 'BNB',
    decimals: 18,
    addresses: {
      1: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52',
      56: null,
      11155111: PLACEHOLDER_ADDRESSES.ZERO_ADDRESS,
      97: null
    },
    logoUrl: '/icons/BNB.svg',
    isStablecoin: false,
    coingeckoId: 'binancecoin'
  }
};

export const SUPPORTED_TOKENS: TokenAddresses = svgFiles.reduce((acc, file) => {
  const symbol = file.replace('.svg', '').replace('.png', '').toUpperCase();
  const existing = existingTokens[symbol];
  
  // Use existing coingeckoId if available, otherwise use symbol lowercase
  const coingeckoId = existing?.coingeckoId || symbol.toLowerCase();
  
  acc[symbol] = {
    symbol,
    name: existing?.name || symbol,
    decimals: existing?.decimals ?? 18,
    addresses: existing?.addresses ?? {1: null, 56: null},
    logoUrl: existing?.logoUrl || '/icons/' + file,
    isStablecoin: existing?.isStablecoin ?? symbol.includes('USD'),
    coingeckoId
  };

  // Special handling for tokens with custom icon mappings
  if (symbol === 'USDP') {
    acc[symbol].logoUrl = '/USDP.svg'; // Use main USDP logo
  }
  
  if (symbol === 'LINK') {
    acc[symbol].logoUrl = '/icons/LINK.svg'; // Use existing uni.svg as fallback for LINK
  }
  
  // Map WBTC to use existing wbtc.svg
  if (symbol === 'WBTC') {
    acc[symbol].logoUrl = '/icons/btc.svg';
  }

  // Update BCH and BTC to use the original btc svg image
  if (symbol === 'BCH') {
    acc[symbol].logoUrl = '/icons/btc.svg';
  }

  if (symbol === 'BTC') {
    acc[symbol].logoUrl = '/icons/btc.svg';
  }

  return acc;
}, {} as TokenAddresses);


/**
 * Get token configuration by symbol
 */
export const getTokenConfig = (symbol: string): TokenConfig | undefined => {
  return SUPPORTED_TOKENS[symbol.toUpperCase()];
};

/**
 * Get token address for a specific network
 */
export const getTokenAddress = (symbol: string, chainId: number): string | null => {
  const token = getTokenConfig(symbol);
  if (!token) return null;
  
  return token.addresses[chainId] || null;
};

/**
 * Get all supported tokens for a specific network
 */
export const getTokensForNetwork = (chainId: number): TokenConfig[] => {
  return Object.values(SUPPORTED_TOKENS).filter(token => {
    const address = token.addresses[chainId];
    // Filter out undefined addresses and placeholder addresses
    return address !== undefined &&
           address !== '0x0000000000000000000000000000000000000000';
  });
};

/**
 * Get all stablecoins
 */
export const getStablecoins = (): TokenConfig[] => {
  return Object.values(SUPPORTED_TOKENS).filter(token => token.isStablecoin);
};

/**
 * Get all non-stablecoin tokens
 */
export const getNonStablecoins = (): TokenConfig[] => {
  return Object.values(SUPPORTED_TOKENS).filter(token => !token.isStablecoin);
};

/**
 * Check if a token is supported on a specific network
 */
export const isTokenSupportedOnNetwork = (symbol: string, chainId: number): boolean => {
  const token = getTokenConfig(symbol);
  if (!token) return false;
  
  return chainId in token.addresses && token.addresses[chainId] !== undefined;
};

/**
 * Check if a token is native to a network (no contract address)
 */
export const isNativeToken = (symbol: string, chainId: number): boolean => {
  const token = getTokenConfig(symbol);
  if (!token) return false;
  
  return token.addresses[chainId] === null;
};

/**
 * Get token decimals for a specific network
 * Some tokens have different decimals on different networks
 */
export const getTokenDecimals = (symbol: string, chainId: number): number => {
  const token = getTokenConfig(symbol);
  if (!token) return 18; // Default to 18 decimals
  
  // Handle special cases where tokens have different decimals on different networks
  if (symbol === 'USDT') {
    if (chainId === 1 || chainId === 11155111) return 6; // Ethereum uses 6 decimals
    if (chainId === 56 || chainId === 97) return 18; // BSC uses 18 decimals
  }
  
  if (symbol === 'USDC') {
    if (chainId === 1 || chainId === 11155111) return 6; // Ethereum uses 6 decimals
    if (chainId === 56 || chainId === 97) return 18; // BSC uses 18 decimals
  }
  
  return token.decimals;
};

/**
 * Get all token symbols
 */
export const getAllTokenSymbols = (): string[] => {
  return Object.keys(SUPPORTED_TOKENS);
};

/**
 * Token display priorities for UI
 */
export const TOKEN_DISPLAY_PRIORITY: { [symbol: string]: number } = {
  USDP: 1,
  USDT: 2,
  USDC: 3,
  ETH: 4,
  BNB: 5
};

/**
 * Get tokens sorted by display priority
 */
export const getTokensSortedByPriority = (): TokenConfig[] => {
  return Object.values(SUPPORTED_TOKENS).sort((a, b) => {
    const priorityA = TOKEN_DISPLAY_PRIORITY[a.symbol] || 999;
    const priorityB = TOKEN_DISPLAY_PRIORITY[b.symbol] || 999;
    return priorityA - priorityB;
  });
};

/**
 * Default token configurations for different networks
 */
export const DEFAULT_TOKENS = {
  1: ['USDP', 'USDT', 'USDC', 'ETH'], // Ethereum
  56: ['USDP', 'USDT', 'USDC', 'BNB'], // BSC
  11155111: ['USDP', 'USDT', 'USDC', 'ETH'], // Sepolia
  97: ['USDP', 'USDT', 'USDC', 'BNB'] // BSC Testnet
};

/**
 * Get default tokens for a network
 */
export const getDefaultTokensForNetwork = (chainId: number): string[] => {
  return DEFAULT_TOKENS[chainId as keyof typeof DEFAULT_TOKENS] || ['USDP', 'USDT', 'USDC'];
};

/**
 * Common token pairs for swapping
 */
export const COMMON_TOKEN_PAIRS = [
  ['USDP', 'USDT'],
  ['USDP', 'USDC'],
  ['USDT', 'USDC'],
  ['ETH', 'USDP'],
  ['ETH', 'USDT'],
  ['ETH', 'USDC'],
  ['BNB', 'USDP'],
  ['BNB', 'USDT'],
  ['BNB', 'USDC']
];

/**
 * Get common pairs for a specific token
 */
export const getCommonPairsForToken = (symbol: string): string[] => {
  return COMMON_TOKEN_PAIRS
    .filter(pair => pair.includes(symbol))
    .map(pair => pair.find(token => token !== symbol))
    .filter(Boolean) as string[];
};
/**
 * Enhanced validation functions for BSC debugging
 */

/**
 * Check if a token address is a placeholder
 */
export const isPlaceholderAddress = (address: string | null): boolean => {
  if (address === null) return false; // Native tokens are not placeholders
  return Object.values(PLACEHOLDER_ADDRESSES).includes(address);
};

/**
 * Get placeholder status for a token on a specific network
 */
export const getTokenPlaceholderStatus = (symbol: string, chainId: number): {
  isPlaceholder: boolean;
  placeholderType?: string;
  warningMessage?: string;
} => {
  const address = getTokenAddress(symbol, chainId);
  
  if (!isPlaceholderAddress(address)) {
    return { isPlaceholder: false };
  }
  
  if (address === PLACEHOLDER_ADDRESSES.USDP_BSC_PLACEHOLDER) {
    return {
      isPlaceholder: true,
      placeholderType: 'USDP_BSC_PLACEHOLDER',
      warningMessage: `⚠️ USDP is using BUSD contract as placeholder on this network. Balances will show BUSD data instead of USDP.`
    };
  }
  
  if (address === PLACEHOLDER_ADDRESSES.ZERO_ADDRESS) {
    return {
      isPlaceholder: true,
      placeholderType: 'ZERO_ADDRESS',
      warningMessage: `⚠️ ${symbol} does not have a valid contract deployment on this network.`
    };
  }
  
  return {
    isPlaceholder: true,
    placeholderType: 'UNKNOWN',
    warningMessage: `⚠️ ${symbol} may be using a placeholder address on this network.`
  };
};

/**
 * Get all placeholder tokens for a specific network
 */
export const getPlaceholderTokensForNetwork = (chainId: number): Array<{
  symbol: string;
  address: string | null;
  placeholderType: string;
  warningMessage: string;
}> => {
  const placeholders: Array<{
    symbol: string;
    address: string | null;
    placeholderType: string;
    warningMessage: string;
  }> = [];
  
  // Check all tokens for placeholders on this network
  Object.values(SUPPORTED_TOKENS).forEach(token => {
    const address = getTokenAddress(token.symbol, chainId);
    const status = getTokenPlaceholderStatus(token.symbol, chainId);
    
    if (status.isPlaceholder && status.placeholderType && status.warningMessage) {
      placeholders.push({
        symbol: token.symbol,
        address,
        placeholderType: status.placeholderType,
        warningMessage: status.warningMessage
      });
    }
  });
  
  return placeholders;
};

/**
 * Get BSC-specific token status
 */
export const getBSCTokenStatus = (): {
  supportedTokens: number;
  placeholderTokens: Array<{
    symbol: string;
    address: string | null;
    placeholderType: string;
    warningMessage: string;
  }>;
  nativeTokens: string[];
  validTokens: string[];
} => {
  const supportedTokens = getTokensForNetwork(56); // BSC mainnet
  const placeholderTokens = getPlaceholderTokensForNetwork(56);
  const nativeTokens: string[] = [];
  const validTokens: string[] = [];
  
  supportedTokens.forEach(token => {
    if (isNativeToken(token.symbol, 56)) {
      nativeTokens.push(token.symbol);
    }
    
    const status = getTokenPlaceholderStatus(token.symbol, 56);
    if (!status.isPlaceholder) {
      validTokens.push(token.symbol);
    }
  });
  
  return {
    supportedTokens: supportedTokens.length,
    placeholderTokens,
    nativeTokens,
    validTokens
  };
};